import { useState, useCallback, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  screenLead,
  screenLeadsBatch,
  type ScreeningResult,
  type BatchScreeningResponse,
} from '../services/leadApi';

/**
 * Hook for managing lead screening state and operations
 *
 * Provides single and batch screening capabilities with progress tracking,
 * result caching, and automatic query invalidation on success.
 */
export function useLeadScreening() {
  const queryClient = useQueryClient();

  // Track which leads are currently being screened
  const [screeningInProgress, setScreeningInProgress] = useState<Map<string, boolean>>(
    () => new Map()
  );

  // Store screening results
  const [screeningResults, setScreeningResults] = useState<Map<string, ScreeningResult>>(
    () => new Map()
  );

  // Track if batch screening is in progress
  const [isBatchScreening, setIsBatchScreening] = useState(false);

  // Single lead screening mutation
  const singleScreenMutation = useMutation({
    mutationFn: screenLead,
    onMutate: (leadId: string) => {
      setScreeningInProgress((prev) => new Map(prev).set(leadId, true));
    },
    onSuccess: (result: ScreeningResult) => {
      // Store the result
      setScreeningResults((prev) => new Map(prev).set(result.lead_id, result));
      // Clear in-progress status
      setScreeningInProgress((prev) => {
        const next = new Map(prev);
        next.delete(result.lead_id);
        return next;
      });
      // Invalidate leads query to refresh data
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['leads'] });
      }
    },
    onError: (_error: Error, leadId: string) => {
      // Clear in-progress status on error
      setScreeningInProgress((prev) => {
        const next = new Map(prev);
        next.delete(leadId);
        return next;
      });
      // Store error result
      setScreeningResults((prev) =>
        new Map(prev).set(leadId, {
          lead_id: leadId,
          success: false,
          error: 'Mutation error',
          error_reason: _error.message,
        })
      );
    },
  });

  // Batch screening mutation
  const batchScreenMutation = useMutation({
    mutationFn: screenLeadsBatch,
    onMutate: (leadIds: string[]) => {
      setIsBatchScreening(true);
      // Mark all leads as in progress
      setScreeningInProgress((prev) => {
        const next = new Map(prev);
        leadIds.forEach((id) => next.set(id, true));
        return next;
      });
    },
    onSuccess: (response: BatchScreeningResponse) => {
      // Store all results
      setScreeningResults((prev) => {
        const next = new Map(prev);
        response.results.forEach((result) => {
          next.set(result.lead_id, result);
        });
        return next;
      });
      // Clear in-progress status for all leads
      setScreeningInProgress((prev) => {
        const next = new Map(prev);
        response.results.forEach((result) => {
          next.delete(result.lead_id);
        });
        return next;
      });
      setIsBatchScreening(false);
      // Invalidate leads query if any succeeded
      if (response.successful > 0) {
        queryClient.invalidateQueries({ queryKey: ['leads'] });
      }
    },
    onError: (_error: Error, leadIds: string[]) => {
      // Clear in-progress status for all leads
      setScreeningInProgress((prev) => {
        const next = new Map(prev);
        leadIds.forEach((id) => next.delete(id));
        return next;
      });
      setIsBatchScreening(false);
      // Store error results for all leads
      setScreeningResults((prev) => {
        const next = new Map(prev);
        leadIds.forEach((id) => {
          next.set(id, {
            lead_id: id,
            success: false,
            error: 'Batch mutation error',
            error_reason: _error.message,
          });
        });
        return next;
      });
    },
  });

  // Screen a single lead
  const screenSingleLead = useCallback(
    (leadId: string) => {
      // Don't re-screen if already in progress
      if (screeningInProgress.get(leadId)) {
        return;
      }
      singleScreenMutation.mutate(leadId);
    },
    [screeningInProgress, singleScreenMutation]
  );

  // Screen multiple leads
  const screenMultipleLeads = useCallback(
    (leadIds: string[]) => {
      // Filter out leads already in progress
      const idsToScreen = leadIds.filter((id) => !screeningInProgress.get(id));
      if (idsToScreen.length === 0) {
        return;
      }
      batchScreenMutation.mutate(idsToScreen);
    },
    [screeningInProgress, batchScreenMutation]
  );

  // Check if a specific lead is being screened
  const isScreening = useCallback(
    (leadId: string): boolean => {
      return screeningInProgress.get(leadId) === true;
    },
    [screeningInProgress]
  );

  // Get screening result for a specific lead
  const getScreeningResult = useCallback(
    (leadId: string): ScreeningResult | undefined => {
      return screeningResults.get(leadId);
    },
    [screeningResults]
  );

  // Check if any screening is in progress
  const isAnyScreening = useMemo(() => {
    return screeningInProgress.size > 0;
  }, [screeningInProgress]);

  // Clear screening result for a lead
  const clearScreeningResult = useCallback((leadId: string) => {
    setScreeningResults((prev) => {
      const next = new Map(prev);
      next.delete(leadId);
      return next;
    });
  }, []);

  // Clear all screening results
  const clearAllScreeningResults = useCallback(() => {
    setScreeningResults(new Map());
  }, []);

  return {
    // State
    screeningInProgress,
    screeningResults,
    isAnyScreening,
    isBatchScreening,

    // Actions
    screenSingleLead,
    screenMultipleLeads,

    // Helpers
    isScreening,
    getScreeningResult,
    clearScreeningResult,
    clearAllScreeningResults,

    // Mutation states (for loading indicators)
    isSingleScreening: singleScreenMutation.isPending,
    isBatchScreeningMutation: batchScreenMutation.isPending,
  };
}

export type UseLeadScreeningReturn = ReturnType<typeof useLeadScreening>;
